import {
  MEAL_PLAN_DAY_ORDER,
  PLANNER_DAY_LONG,
  PLANNER_DAY_LETTER,
  calendarMealShortLabel,
  defaultPlannerCalendarContext,
} from '../../utils/mealPlanHelpers.js';
import { countWeekCoverage, isNightOffMeal, nightOffLabel, weekCoverageLabel } from '../../utils/nightOff.js';
import { textColorForSwatch } from '../../utils/plannerSwatchContrast.js';
import './planner.css';

/**
 * Part C — Option 1 week-strip list header.
 * Slim read-only card: day letter + recipe-swatch bar (or muted dash if open).
 * Replaces the old CalendarStrip above the Planned list.
 */
export default function PlanWeekListHeader({
  assignedByDay = null,
  dayDates = null,
  todayKey = undefined,
  density = 'desktop',
  dimmed = false,
}) {
  const fallback = defaultPlannerCalendarContext();
  const dates = dayDates ?? fallback.dayDates;
  const effectiveToday = todayKey === undefined ? fallback.todayKey : todayKey;
  const compact = density === 'mobile';
  const coverage = countWeekCoverage(assignedByDay);
  const coverageText = weekCoverageLabel(coverage);

  return (
    <div
      className={[
        'pwh-card',
        compact ? 'pwh-card--compact' : '',
        dimmed ? 'pwh-card--dimmed' : '',
      ].filter(Boolean).join(' ')}
      aria-label={`This week · ${coverageText}`}
    >
      <div className="pwh-header">
        <span className="pwh-kicker">This week</span>
        <span className="pwh-coverage">
          <span className="pwh-coverage-count">{coverage.cook}</span>
          {' cook · '}
          <span className="pwh-coverage-count">{coverage.nightOff}</span>
          {coverage.nightOff === 1 ? ' night off' : ' nights off'}
          {' · '}
          <span className="pwh-coverage-open">{coverage.open} open</span>
        </span>
      </div>
      <div className="pwh-ticks" role="list">
        {MEAL_PLAN_DAY_ORDER.map((dayKey) => {
          const meal = assignedByDay?.[dayKey] || null;
          const letter = PLANNER_DAY_LETTER[dayKey] || dayKey[0];
          const longLabel = PLANNER_DAY_LONG[dayKey] || dayKey;
          const dateNum = dates?.[dayKey];
          const isToday = effectiveToday != null && dayKey === effectiveToday;
          const nightOff = isNightOffMeal(meal);
          const tag = nightOff
            ? nightOffLabel(meal.nightOffType)
            : meal
              ? calendarMealShortLabel(meal.name, { shortLabel: meal.shortLabel })
              : '';
          const swatch = nightOff ? null : (meal?.swatch || null);
          const barStyle = swatch
            ? { background: swatch, color: textColorForSwatch(swatch) }
            : undefined;

          return (
            <div
              key={dayKey}
              className="pwh-daycol"
              role="listitem"
              title={
                meal
                  ? `${longLabel}${dateNum != null ? ` ${dateNum}` : ''} · ${meal.name}`
                  : `${longLabel}${dateNum != null ? ` ${dateNum}` : ''} · open`
              }
            >
              <span className="pwh-daylabel" aria-hidden="true">{letter}</span>
              <span
                className={[
                  'pwh-swatchbar',
                  isToday ? 'is-today' : '',
                  nightOff ? 'is-night-off' : '',
                ].filter(Boolean).join(' ')}
                style={barStyle}
                aria-hidden="true"
              >
                {meal ? tag : '\u2013'}
              </span>
              <span className="sr-only">
                {meal
                  ? `${longLabel}${dateNum != null ? `, ${dateNum}` : ''}: ${meal.name}`
                  : `${longLabel}${dateNum != null ? `, ${dateNum}` : ''}: open`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
